import "@fontsource-variable/archivo";
import "@fontsource-variable/recursive";
import { initPage } from "./page";
import "./style.css";

const messages = {
  en: {
    "nav.overview": "Overview",
    "nav.endpoints": "Endpoints",
    "nav.errors": "Errors",
    "nav.backend": "Build a backend",
    "theme.toDark": "Switch to dark theme",
    "theme.toLight": "Switch to light theme",
    "doc.eyebrow": "Protocol reference",
    "doc.title": "The HTTP v1 protocol.",
    "doc.lede": "Six read-only JSON endpoints. Implement them in any language and <web-git-graph> can render your history — no SDK required.",
    "doc.fact1.term": "Media type",
    "doc.fact2.term": "Access",
    "doc.fact2.desc": "Read-only — no Git mutation exists in the contract",
    "doc.fact3.term": "Versioning",
    "doc.fact3.desc": "All routes live under /v1; the media type carries the version",
    "quick.kicker": "Quick start",
    "quick.title": "Two sides of the wire.",
    "quick.description": "The reference server ships with @web-git-graph/node; the browser client with @web-git-graph/web. Either side can be replaced.",
    "quick.server": "Serve a repository",
    "quick.serverNote": "Binds to 127.0.0.1 by default. CORS stays off unless you enable it.",
    "quick.client": "Connect the component",
    "quick.clientNote": "The provider sends Accept: application/vnd.web-git-graph.v1+json and handles paging, comparison, and diffs.",
    "endpoints.kicker": "Endpoints",
    "endpoints.title": "Six routes, one contract.",
    "endpoints.description": "Repository ids are opaque tokens minted by the server — filesystem paths never cross the wire. Cursors are opaque too; treat them as bookmarks, not offsets.",
    "params.name": "Query",
    "params.desc": "Description",
    "ep.capabilities.desc": "Feature discovery. The component reads this once and hides anything the backend does not support.",
    "ep.repositories.desc": "Lists the repositories this server exposes. The id is the only handle a client ever holds.",
    "ep.history.desc": "One page of commits plus the refs that point into it. Follow cursor while hasMore is true; lanes stay stable across pages.",
    "ep.history.ref": "Branch, tag, or commit to start from (default: HEAD)",
    "ep.history.cursor": "Opaque cursor from the previous page",
    "ep.history.limit": "Page size, capped by maxPageSize",
    "ep.history.wt": "Include the working-tree row (default: true)",
    "ep.commit.desc": "Full details for one commit: message body, refs, and the changed files. Use the literal oid __WORKTREE__ for the working tree.",
    "ep.compare.desc": 'The tree difference between two revisions. A revision is {"kind":"commit","oid":…}, {"kind":"stash","oid":…}, or {"kind":"working-tree"}.',
    "ep.diff.desc": "A unified patch for a single file, loaded lazily when a row is expanded. Oversized output is truncated instead of failing.",
    "errors.kicker": "Errors",
    "errors.title": "One error shape.",
    "errors.description": "Every non-2xx response carries the same body. retryable tells the client whether trying again can help; a 409 snapshot_expired means the cursor is stale — restart from the first page.",
    "errors.meaning": "Meaning",
    "err.bad_request": "Malformed route, query, or body",
    "err.unauthorized": "The authorize hook rejected the request",
    "err.forbidden": "Authenticated but not allowed",
    "err.repository_not_found": "Unknown repository id",
    "err.revision_not_found": "Unknown commit, stash, or ref",
    "err.snapshot_expired": "Cursor no longer valid — reload from page one",
    "err.output_limit": "Request or response exceeded a size limit",
    "err.unsupported": "The backend does not implement this capability",
    "err.rate_limited": "Too many requests — retry later",
    "err.internal_error": "Unexpected server failure",
    "err.git_unavailable": "Git could not be executed on the server",
    "backend.kicker": "Build a backend",
    "backend.title": "Any language works.",
    "backend.description": "The contract is just HTTP and JSON. Implement the six routes in Go, Rust, Python, Java — whatever already runs in your stack.",
    "backend.schema.title": "Typed contract, exported",
    "backend.schema.body": "The protocol package ships the DTOs, JSON Schemas, and the OpenAPI document — use them to validate your implementation.",
    "backend.node.title": "Or reuse the Node handler",
    "backend.node.body": "A fetch-style handler with an authorize hook drops into Node, and the local backend is hardened: opaque ids, no shell, size limits.",
    "backend.rule1": "Respond with content-type application/vnd.web-git-graph.v1+json and cache-control: no-store.",
    "backend.rule2": "Never expose filesystem paths — mint opaque repository ids instead.",
    "backend.rule3": "Keep cursors opaque and return snapshot_expired when they go stale.",
    "backend.rule4": "Advertise only what you implement in /v1/capabilities — the UI adapts.",
    "closing.title": "Six endpoints between Git and your UI.",
    "closing.demo": "Open the live demo",
    "closing.github": "View on GitHub ↗",
    "footer.tagline": "Framework-free Git history for the web.",
    "common.copy": "Copy",
    "common.copied": "Copied"
  },
  zh: {
    "nav.overview": "返回首页",
    "nav.endpoints": "端点",
    "nav.errors": "错误模型",
    "nav.backend": "实现后端",
    "theme.toDark": "切换到深色主题",
    "theme.toLight": "切换到浅色主题",
    "doc.eyebrow": "协议参考",
    "doc.title": "HTTP v1 协议。",
    "doc.lede": "六个只读 JSON 端点。用任何语言实现它们,<web-git-graph> 就能渲染你的历史——无需 SDK。",
    "doc.fact1.term": "媒体类型",
    "doc.fact2.term": "访问级别",
    "doc.fact2.desc": "只读——契约中不存在任何 Git 写操作",
    "doc.fact3.term": "版本策略",
    "doc.fact3.desc": "所有路由位于 /v1 之下,版本由媒体类型携带",
    "quick.kicker": "快速开始",
    "quick.title": "协议的两端。",
    "quick.description": "参考服务端由 @web-git-graph/node 提供,浏览器客户端由 @web-git-graph/web 提供,两端都可以替换成你自己的实现。",
    "quick.server": "启动服务端",
    "quick.serverNote": "默认只监听 127.0.0.1;CORS 需显式开启。",
    "quick.client": "连接组件",
    "quick.clientNote": "Provider 会发送 Accept: application/vnd.web-git-graph.v1+json,并自动处理分页、比较与 diff。",
    "endpoints.kicker": "端点",
    "endpoints.title": "六个路由,一份契约。",
    "endpoints.description": "仓库 id 是服务端生成的不透明令牌——文件系统路径永远不会出现在协议上。游标同样不透明:把它当书签,而不是偏移量。",
    "params.name": "查询参数",
    "params.desc": "说明",
    "ep.capabilities.desc": "能力发现。组件启动时读取一次,自动隐藏后端不支持的功能。",
    "ep.repositories.desc": "列出服务端暴露的仓库。id 是客户端持有的唯一句柄。",
    "ep.history.desc": "一页提交及指向它们的 ref。当 hasMore 为 true 时携带 cursor 继续请求;泳道跨分页保持稳定。",
    "ep.history.ref": "起始分支、标签或提交(默认 HEAD)",
    "ep.history.cursor": "上一页返回的不透明游标",
    "ep.history.limit": "分页大小,受 maxPageSize 限制",
    "ep.history.wt": "是否包含工作区行(默认 true)",
    "ep.commit.desc": "单个提交的完整详情:消息正文、ref 与变更文件。工作区使用特殊 oid __WORKTREE__。",
    "ep.compare.desc": '两个修订之间的树差异。修订为 {"kind":"commit","oid":…}、{"kind":"stash","oid":…} 或 {"kind":"working-tree"}。',
    "ep.diff.desc": "单个文件的 unified patch,在展开文件行时按需加载。超大输出会被截断而不是失败。",
    "errors.kicker": "错误模型",
    "errors.title": "统一的错误结构。",
    "errors.description": "所有非 2xx 响应携带相同的结构。retryable 表示重试是否有意义;409 snapshot_expired 表示游标已失效——从第一页重新加载。",
    "errors.meaning": "含义",
    "err.bad_request": "路由、查询参数或请求体格式错误",
    "err.unauthorized": "authorize 钩子拒绝了请求",
    "err.forbidden": "已认证但无权访问",
    "err.repository_not_found": "未知的仓库 id",
    "err.revision_not_found": "未知的提交、stash 或 ref",
    "err.snapshot_expired": "游标已失效——从第一页重新加载",
    "err.output_limit": "请求或响应超出大小限制",
    "err.unsupported": "后端未实现该能力",
    "err.rate_limited": "请求过于频繁——稍后重试",
    "err.internal_error": "服务端意外错误",
    "err.git_unavailable": "服务端无法执行 Git",
    "backend.kicker": "实现后端",
    "backend.title": "任何语言都可以。",
    "backend.description": "契约只是 HTTP 加 JSON。用 Go、Rust、Python、Java——你技术栈里现成的东西实现这六个路由即可。",
    "backend.schema.title": "类型化契约,直接导出",
    "backend.schema.body": "protocol 包内置 DTO、JSON Schema 与 OpenAPI 文档,可直接用来校验你的实现。",
    "backend.node.title": "或复用 Node handler",
    "backend.node.body": "fetch 风格的 handler 带 authorize 钩子,可直接嵌入 Node;本地后端已加固:不透明 id、不经过 shell、有大小限制。",
    "backend.rule1": "响应使用 content-type application/vnd.web-git-graph.v1+json 与 cache-control: no-store。",
    "backend.rule2": "永远不要暴露文件系统路径——用不透明的仓库 id 代替。",
    "backend.rule3": "保持游标不透明,失效时返回 snapshot_expired。",
    "backend.rule4": "在 /v1/capabilities 中只声明已实现的能力——UI 会自动适配。",
    "closing.title": "Git 与界面之间,只隔六个端点。",
    "closing.demo": "打开在线演示",
    "closing.github": "在 GitHub 查看 ↗",
    "footer.tagline": "为 Web 而生的零框架 Git 历史图。",
    "common.copy": "复制",
    "common.copied": "已复制"
  }
};

initPage({
  messages,
  documentTitle: {
    en: "HTTP v1 protocol — Web Git Graph",
    zh: "HTTP v1 协议 — Web Git Graph"
  }
});
